// src/main/shell/symbolGrid/stubDataSource.ts
//
// A self-contained FAKE controller so the ported SymbolGrid renders + ticks for
// verification with NO backend. It synthesizes a realistic S7-style symbol tree
// (DB blocks → structs → typed leaves), drives live values on a timer, and keeps
// a per-symbol circular buffer so the sparklines animate exactly as they do in
// the agent.
//
// ──────────────────────────────────────────────────────────────────────────────
// TODO(real-backend): THIS IS WHERE THE REAL WORKER STREAMS PLUG IN.
//
//   The grid consumes the `SymbolGridDataSource` interface. The real data layer
//   (app-ui's Backend Worker) exposes two streams over MessagePorts:
//
//     backend.api.subscribeSymbols(controllerId) → StreamFrame<SymbolSummary>
//         (today a paginated SUMMARY: totalInScope + sample paths. The grid
//          needs the FULL catalog hierarchy — type / permission / parent / child
//          edges per symbol. That richer SymbolPage shape lands when
//          SymbolIndex.Subscribe is wired through; see core/bridge/connect-client.ts
//          `subscribeSymbols` + nats-fabric-symbol-indexer.md SymbolRow/SymbolPage.)
//
//     backend.api.subscribeValues(controllerId) → StreamFrame<LiveValue>
//         ({ symbolId, text } today; the grid wants the TYPED value + a timestamp
//          so it can push samples into each symbol's circular buffer for charts.)
//
//   To wire it, write a `WorkerSymbolDataSource implements SymbolGridDataSource`
//   that:
//     1. opens both ports (mirror vitesse-controller-detail.ts's open()),
//     2. folds the symbol-catalog frames into the same hierarchy maps this stub
//        builds (rootIds / childrenById / parentById / metaById / subtreeSize),
//        firing 'symbolsDiscovered' / 'symbolsChunkInserted' / 'symbolsFetchComplete'
//        as pages arrive (so SymbolTreeView rebuilds incrementally — it already
//        listens for exactly these events),
//     3. on each value frame, updates `values`, pushes (now, numeric) into that
//        symbol's circular buffer (bumping minMaxVersion), and bumps the change
//        cursor + 'symbolLabelChanged' as applicable.
//   The grid + tree code does NOT change — only this source swaps. Then drop the
//   stub from <vitesse-controller-detail>.
// ──────────────────────────────────────────────────────────────────────────────

import { EventEmitter } from './emitter';
import type {
  SymbolGridDataSource,
  SymbolMeta,
  SymbolValue,
  ChartBufferRaw,
  ControllerChartConfig,
  IteratedSymbol,
  SymbolChanges,
  SymbolDataEvent,
  SymbolDataListener,
} from './dataSource';

interface FakeSymbol {
  id: string;
  meta: SymbolMeta;
  children: string[];
  parent: string | null;
  depth: number;
}

// Circular buffer for one symbol's chart history (30s @ ~5Hz ≈ 150 samples; we
// size to 256 to be safe and match the agent's "30 min × 50Hz" pattern at a
// smaller scale for the stub).
const BUFFER_CAPACITY = 512;

interface SymbolBuffer {
  timestamps: Float64Array;
  values: Float64Array;
  count: number;
  head: number;
  minMaxVersion: number;
}

const TICK_MS = 200; // value update cadence (5 Hz)
const CHART_CONFIG: ControllerChartConfig = {
  timeWindowMs: 30000,
  sampleRateHz: 5,
  theme: 'frost',
};

export class StubSymbolDataSource extends EventEmitter implements SymbolGridDataSource {
  readonly controllerId: string;

  private symbols = new Map<string, FakeSymbol>();
  private rootIds = new Set<string>();
  private subtreeSizeCache = new Map<string, number>();

  private values = new Map<string, SymbolValue>();
  private labels = new Map<string, string>();
  // The slice of chartable leaves whose values this stub animates (demo data).
  // Everything is "subscribed"; this is just which symbols tick for the charts.
  private animating = new Set<string>();
  private buffers = new Map<string, SymbolBuffer>();

  // structural change cursor (labels / subscriptions)
  private changeVersion = 0;
  private changedSymbols = new Set<string>();

  private tickTimer: ReturnType<typeof setInterval> | null = null;
  // per-symbol oscillator state so values move smoothly
  private phase = new Map<string, number>();

  constructor(controllerId: string) {
    super();
    this.controllerId = controllerId;
    this.build();
  }

  /** Start the live-value tick + emit a discovery→fetchComplete cycle. */
  start(): void {
    // Mimic the real discovery handshake the tree listens for.
    this.emit('symbolsDiscovered');
    this.emit('symbolsFetchComplete');
    this.emit('symbolDiscoveryFinished');

    if (this.tickTimer) return;
    this.tickTimer = setInterval(() => this.tick(), TICK_MS);
  }

  stop(): void {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    this.removeAllListeners();
  }

  // ── hierarchy ───────────────────────────────────────────────────────────────
  getRootSymbols(): Set<string> {
    return this.rootIds;
  }
  getChildren(symbolId: string): Set<string> {
    const s = this.symbols.get(symbolId);
    return new Set(s ? s.children : []);
  }
  getSubtreeSize(symbolId: string): number {
    const cached = this.subtreeSizeCache.get(symbolId);
    if (cached !== undefined) return cached;
    const s = this.symbols.get(symbolId);
    if (!s) return 0;
    let size = 1;
    for (const c of s.children) size += this.getSubtreeSize(c);
    this.subtreeSizeCache.set(symbolId, size);
    return size;
  }
  getDepth(symbolId: string): number {
    return this.symbols.get(symbolId)?.depth ?? 0;
  }
  getParent(symbolId: string): string | undefined {
    return this.symbols.get(symbolId)?.parent ?? undefined;
  }
  isStreamingSymbols(): boolean {
    return false;
  }

  // ── per-symbol read ───────────────────────────────────────────────────────
  getSymbol(symbolId: string): SymbolMeta | undefined {
    return this.symbols.get(symbolId)?.meta;
  }
  getValue(symbolId: string): SymbolValue {
    return this.values.get(symbolId);
  }
  getLabel(symbolId: string): string | undefined {
    return this.labels.get(symbolId);
  }
  getSymbolBufferRaw(symbolId: string): ChartBufferRaw | null {
    const buf = this.buffers.get(symbolId);
    if (!buf || buf.count === 0) return null;
    return {
      timestamps: buf.timestamps,
      values: buf.values,
      count: buf.count,
      head: buf.head,
      capacity: BUFFER_CAPACITY,
      minMaxVersion: buf.minMaxVersion,
      nowMs: Date.now(),
      scaleMin: NaN, // stub: let the renderer scan its own windowed min/max
      scaleMax: NaN,
    };
  }
  getControllerChartConfig(): ControllerChartConfig {
    return CHART_CONFIG;
  }

  // ── flat scans ──────────────────────────────────────────────────────────────
  *iterateSymbols(): Iterable<IteratedSymbol> {
    for (const s of this.symbols.values()) {
      yield { symbol_id: s.id, type: s.meta.type, permission: s.meta.permission };
    }
  }

  // ── change tracking ───────────────────────────────────────────────────────
  getChanges(_sinceVersion: number): SymbolChanges {
    return { version: this.changeVersion, changedSymbols: this.changedSymbols };
  }
  clearConsumedChanges(): void {
    this.changedSymbols = new Set();
  }

  // ── label write (stub: store locally + fire change) ─────────────────────────
  setLabel(symbolId: string, label: string | undefined): void {
    if (label) this.labels.set(symbolId, label);
    else this.labels.delete(symbolId);
    this.markChanged(symbolId);
    this.emit('symbolLabelChanged');
  }

  // ── events ────────────────────────────────────────────────────────────────
  on(event: SymbolDataEvent, listener: SymbolDataListener): this {
    return super.on(event, listener);
  }
  off(event: SymbolDataEvent, listener: SymbolDataListener): this {
    return super.off(event, listener);
  }

  // ── internals ───────────────────────────────────────────────────────────────

  private markChanged(symbolId: string): void {
    this.changeVersion++;
    this.changedSymbols.add(symbolId);
  }

  private makeBuffer(): SymbolBuffer {
    return {
      timestamps: new Float64Array(BUFFER_CAPACITY),
      values: new Float64Array(BUFFER_CAPACITY),
      count: 0,
      head: 0,
      minMaxVersion: 0,
    };
  }

  private push(buf: SymbolBuffer, ts: number, v: number): void {
    const writeIdx = (buf.head + buf.count) % BUFFER_CAPACITY;
    if (buf.count < BUFFER_CAPACITY) {
      buf.count++;
    } else {
      buf.head = (buf.head + 1) % BUFFER_CAPACITY; // overwrite oldest
    }
    buf.timestamps[writeIdx] = ts;
    buf.values[writeIdx] = v;
    buf.minMaxVersion++;
  }

  /** Advance every subscribed symbol's value + chart buffer. */
  private tick(): void {
    const now = Date.now();
    for (const id of this.animating) {
      const meta = this.symbols.get(id)?.meta;
      if (!meta) continue;

      let numeric: number;
      let value: SymbolValue;
      const ph = (this.phase.get(id) ?? Math.random() * Math.PI * 2) + 0.12;
      this.phase.set(id, ph);

      switch (meta.type) {
        case 'boolean': {
          const b = Math.sin(ph) > 0;
          value = b;
          numeric = b ? 1 : 0;
          break;
        }
        case 'integer': {
          const n = Math.round(50 + 45 * Math.sin(ph) + (Math.random() - 0.5) * 4);
          value = n;
          numeric = n;
          break;
        }
        default: {
          // number
          const n = 50 + 40 * Math.sin(ph) + (Math.random() - 0.5) * 3;
          value = Math.round(n * 1000) / 1000;
          numeric = n;
          break;
        }
      }

      this.values.set(id, value);
      let buf = this.buffers.get(id);
      if (!buf) {
        buf = this.makeBuffer();
        this.buffers.set(id, buf);
      }
      this.push(buf, now, numeric);
    }
  }

  /**
   * Build a synthetic S7-style symbol tree:
   *   DB1..DB6 (root data blocks)
   *     └─ Motors / Sensors / Valves / Config / Diagnostics (structs)
   *          └─ leaves: typed scalars (bool/int/real/string)
   * ~6 blocks × ~5 structs × ~12 leaves ≈ 360 symbols — enough to exercise
   * virtualization, expand/collapse, search, sort, and charts.
   */
  private build(): void {
    const groups = ['Motors', 'Sensors', 'Valves', 'Config', 'Diagnostics'];
    const leafSpecs: { suffix: string; type: string; perm: string }[] = [
      { suffix: 'Speed', type: 'number', perm: 'rw' },
      { suffix: 'Setpoint', type: 'number', perm: 'rw' },
      { suffix: 'Current', type: 'number', perm: 'r' },
      { suffix: 'Temperature', type: 'number', perm: 'r' },
      { suffix: 'Pressure', type: 'number', perm: 'r' },
      { suffix: 'Position', type: 'integer', perm: 'rw' },
      { suffix: 'Count', type: 'integer', perm: 'r' },
      { suffix: 'Running', type: 'boolean', perm: 'r' },
      { suffix: 'Fault', type: 'boolean', perm: 'r' },
      { suffix: 'Enable', type: 'boolean', perm: 'rw' },
      { suffix: 'Mode', type: 'string', perm: 'rw' },
      { suffix: 'Name', type: 'string', perm: 'r' },
    ];

    const addSymbol = (id: string, meta: SymbolMeta, parent: string | null, depth: number): void => {
      this.symbols.set(id, { id, meta, children: [], parent, depth });
      if (parent === null) this.rootIds.add(id);
      else this.symbols.get(parent)!.children.push(id);
    };

    const containerMeta: SymbolMeta = { type: 'object', permission: 'r' };

    for (let db = 1; db <= 6; db++) {
      const dbId = `DB${db}`;
      addSymbol(dbId, containerMeta, null, 0);

      for (const group of groups) {
        const groupId = `${dbId}.${group}`;
        addSymbol(groupId, containerMeta, dbId, 1);

        // a couple of instances per group → deeper nesting on the bigger blocks
        const instances = db <= 3 ? 3 : 2;
        for (let inst = 1; inst <= instances; inst++) {
          const instId = `${groupId}.${group.slice(0, -1)}${inst}`;
          addSymbol(instId, containerMeta, groupId, 2);

          for (const leaf of leafSpecs) {
            const leafId = `${instId}.${leaf.suffix}`;
            const meta: SymbolMeta = { type: leaf.type, permission: leaf.perm };
            addSymbol(leafId, meta, instId, 3);

            // seed an initial value for every leaf so the value column is populated
            this.values.set(leafId, this.seedValue(leaf.type));

            // auto-subscribe a slice of numeric/bool leaves so charts animate
            // immediately on first render (mirrors the "everything subscribed"
            // direction without overwhelming the stub).
            if (leaf.type !== 'string' && (inst === 1 || leaf.suffix === 'Speed' || leaf.suffix === 'Running')) {
              this.animating.add(leafId);
              this.buffers.set(leafId, this.makeBuffer());
            }
          }
        }
      }
    }

    // a couple of seed labels so the Label column has content to sort/search on
    const first = [...this.symbols.keys()].find((id) => id.endsWith('.Speed'));
    if (first) this.labels.set(first, 'Conveyor speed');
  }

  private seedValue(type: string): SymbolValue {
    switch (type) {
      case 'boolean':
        return Math.random() > 0.5;
      case 'integer':
        return Math.round(Math.random() * 100);
      case 'string':
        return ['AUTO', 'MANUAL', 'IDLE', 'RUN'][Math.floor(Math.random() * 4)];
      default:
        return Math.round(Math.random() * 100 * 1000) / 1000;
    }
  }
}
